
import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, AfterViewInit, ViewChild, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { routerTransition } from './_animations/router-transition.animation';
import { trigger, animate, style, transition } from '@angular/animations';
import { AuthService } from './auth/auth.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SessionService } from './session/session.service';
import { TranslateService } from '@ngx-translate/core';
import { DomSanitizer } from "@angular/platform-browser";

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    animations: [
        routerTransition,
        trigger('enterAnimation', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('500ms ease-in-out', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                style({ opacity: 1 }),
                animate('500ms 300ms ease-in-out', style({ opacity: 0 }))
            ])
        ])
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class AppComponent implements OnDestroy, OnInit, AfterViewInit {
  @ViewChild('appBackground', { static: false })
  public background: ElementRef;
  private routerSubscription: Subscription;
  public routerState: string;
  public windowScrolled: boolean;

  constructor(
    protected authService: AuthService,
    protected sessionService: SessionService,
    protected translate: TranslateService,
    protected domSanitizer: DomSanitizer,
    protected router: Router,
    @Inject(DOCUMENT) protected document: Document) {
    this.routerSubscription = router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(
        (navigationEnd: NavigationEnd) => {
          // vi är inte intresserade av tracka dessa
          if (
            navigationEnd.url === '/home' ||
            navigationEnd.url === '/borgenar' ||
            navigationEnd.url === '/login' ||
            navigationEnd.url === '/more-info' ||
            navigationEnd.url === '/personal-data' ||
            navigationEnd.url === '/not-found' ||
            navigationEnd.url === '/select-instance' ||
            navigationEnd.url.startsWith('/select-instance?') ||
            navigationEnd.url.startsWith('/admin-')) {
            return;
          }

          if (navigationEnd.url
            && sessionService.hasActiveInstance
            && sessionService.activeInstance.defaultUrl !== navigationEnd.url) {
            sessionService.activeInstance.defaultUrl = navigationEnd.url;
            sessionService.storeSession();
          }
        });
  }

  public ngOnInit() {
    this.translate.use(this.sessionService.getLanguage()).subscribe();
  }

  public ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  public ngAfterViewInit(): void {
    this.setTodaysBackground();
    this.scrollToTop();
  }

  public getState(outlet: RouterOutlet): string {
    return outlet.activatedRouteData.state;
  }

  private getCurrentScroll(): number {
    return this.document.documentElement.scrollTop || this.document.body.scrollTop;
  }

  @HostListener("window:scroll", [])
  protected onWindowScroll(): void {
    // Vi är mer än 1,5 skärmhöjder nerscrollade
    this.windowScrolled = this.getCurrentScroll() > this.document.defaultView.innerHeight * 1.5;
  }

  public scrollToTop(): void {
    const divider = 10;
    const smoothscroll = (): void => {
      const currentScroll = this.getCurrentScroll();
      if (currentScroll >= divider) {
        this.document.defaultView.requestAnimationFrame(smoothscroll);
        this.document.defaultView.scrollTo(0, currentScroll - (currentScroll / divider));
      }
      else {
        this.document.defaultView.scrollTo(0, 0);
      }
    }

    smoothscroll();
  }

  private setTodaysBackground(): void {
    const backgrounds = [{
      dir: 'winter',
      files: [
        'alireza-badiee-1340168',
        'anders-jilden-178236',
        'andrea-windolph-437185',
        'andrea-windolph-452731',
        'andreas-dress-769672',
        'andreas-eriksson-1132313',
        'andreas-eriksson-1282602',
        'billy-berg-703490',
        'david-becker-670925',
        'enza-brunero-1088389',
        'gabriel-alenius-1331597',
        'graham-padmore-541697',
        'ivars-utinsns-723615',
        'jaanus-jagomagi-1245518',
        'jaanus-jagomagi-1267435',
        'jasper-graetsch-546239',
        'jeena-paradies-512478',
        'jens-johnsson-684377',
        'jon-flobrant-68692',
        'jonas-jacobsson-341342',
        'jonathan-brinkhorst-678155',
        'jordan-bannerman-540831',
        'lina-loos-1115580',
        'marcus-lofvenberg-451334',
        'marcus-lofvenberg-451618',
        'mikael-stenberg-786238',
        'nathaniel-shuman-366346',
        'niclas-lundin-563274',
        'oscar-soderlund-159360',
        'pigoff-photography-195118',
        'pigoff-photography-198916',
        'robert-gramner-718500',
        'robert-larsson-64547',
        'tamara-menzi-1260124',
        'tamara-schipchinskaya-514691',
        'theodor-lundqvist-530411',
        'valeriia-kryshchuk-1239253']
    }, {
      dir: 'spring',
      files: [
        'anders-wetterstam-668917',
        'andreas-dress-732843',
        'andreas-eriksson-1132322',
        'claudio-schwarz-purzlbaum-1215818',
        'elina-bernpaintner-1081700',
        'etienne-pauthenet-123677',
        'florian-rieder-447778',
        'frederik-falinski-728581',
        'geran-de-klerk-136351',
        'geran-de-klerk-263475',
        'joacim-bohlander-560488',
        'johannes-hurtig-421923',
        'jon-flobrant-336278',
        'jon-flobrant-65431',
        'jonas-jacobsson-1207604',
        'jonas-jacobsson-338018',
        'jonas-jacobsson-351836',
        'jonas-jacobsson-468594',
        'jonas-jacobsson-490559',
        'jonas-jacobsson-545905',
        'julian-hochgesang-795088',
        'julien-leclercq-1228418',
        'karen-hammega-548922',
        'karl-jk-hedin-1056785',
        'lasse-moller-1080137',
        'magnus-ostberg-695615',
        'manuel-nageli-571461',
        'mikael-stenberg-1177522',
        'miller-jacob-715141',
        'natalia-y-339474',
        'oscar-helgstrand-1138481',
        'oscar-nilsson-601470',
        'oscar-nord-696046',
        'oskars-sylwan-670118',
        'pia-nystrom-1174287',
        'pierre-chatel-innocenti-498179',
        'pierre-chatel-innocenti-522586',
        'riaz-gorbaz-973778',
        'robert-bye-462773',
        'robert-bye-462774',
        'sebastian-sandqvist-418547',
        'stefan-gunnarsson-159330',
        'ulf-bodin-524855',
        'yeo-khee-508835']
    }, {
      dir: 'summer',
      files: [
        'anders-jilden-108186',
        'billy-berg-703488',
        'carl-nenzen-loven-326846',
        'christian-widell-28959',
        'christian-widell-97918',
        'claudio-buttler-1356731',
        'elina-bernpaintner-1080375',
        'fredrik-ohlander-679159',
        'gemma-evans-62123',
        'georgy-rudakov-558601',
        'ghaith-amouri-756719',
        'hakan-tas-790794',
        'henrik-berg-1096777',
        'jacob-shutler-1185522',
        'janko-ferlic-223224',
        'jasper-graetsch-548275',
        'javi-arji-723948',
        'jon-flobrant-183186',
        'jon-flobrant-319186',
        'jon-flobrant-42712',
        'jon-flobrant-65429',
        'julius-jansson-1243765',
        'karl-1170718',
        'kasya-shahovskaya-733469',
        'ludde-lorentz-47866',
        'marten-bjork-623852',
        'marten-bjork-637521',
        'marten-bjork-726400',
        'marten-hagberg-737242',
        'max-hermansson-415034',
        'max-hermansson-761949',
        'oscar-helgstrand-1089751',
        'oscar-nord-355721',
        'oskars-sylwan-692982',
        'paul-povoroznuk-1168406',
        'pierre-chatel-innocenti-548845',
        'robert-bye-462765',
        'shubhesh-aggarwal-463344',
        'steven-erixon-747043',
        'steven-erixon-747285']
    }, {
      dir: 'fall',
      files: [
        'agnieszka-boeske-359706',
        'alexei-scutari-803901',
        'anders-jilden-295809',
        'andreas-eriksson-1175995',
        'carl-nenzen-loven-335381',
        'cristina-munteanu-39306',
        'david-aler-12588',
        'eskil-svensson-248732',
        'ferhat-deniz-fors-782312',
        'frederik-falinski-714337',
        'fredrik-ohlander-624957',
        'geran-de-klerk-136360',
        'geran-de-klerk-210678',
        'guillaume-briard-748569',
        'ines-d-anselme-419417',
        'johan-rydberg-110346',
        'johannes-w-222351',
        'jon-flobrant-183182',
        'jon-flobrant-229726',
        'jon-flobrant-283324',
        'jonas-jacobsson-1316804',
        'jonathan-petersson-206356',
        'jonathan-petersson-737847',
        'jordan-bannerman-1152647',
        'karlo-boni-1146366',
        'lasse-moller-1081624',
        'luka-slapnicar-1197743',
        'lukas-ganster-1298448',
        'marcus-bengtsson-1124921',
        'marten-bjork-1146043',
        'marten-bjork-546739',
        'marten-bjork-710443',
        'mikael-stenberg-1166584',
        'mikael-stenberg-1166587',
        'mikael-stenberg-1166588',
        'mikael-stenberg-1177521',
        'nathaniel-shuman-396946',
        'noukka-signe-1204228',
        'oskars-sylwan-692974',
        'raphael-andres-430356',
        'samuel-elias-794614',
        'steven-erixon-749658',
        'susan-yin-647448',
        'teddy-osterblom-751595']
    }];

    function onLoad(image: HTMLImageElement): void {
      const background = document.getElementById('app-background');
      background.style.backgroundImage = `url('${image.src}')`;
      background.style.opacity = '1';
      image = null;
    }

    function preloadBackground(imageUrl: string): void {
      let image = new Image();
      image.onload = () => onLoad(image);
      image.onerror = () => console.error(`Could not load background image: '${imageUrl}'`);
      image.src = imageUrl;
    }

    // getTimezoneOffset() returnerar antalet minuter som UTC ligger före lokal tid.
    // Vi multiplicerar med 6e4 = 60 000 för att få det i millisekunder.
    // Vi subtraherar sedan 108e5 = 10 800 000 millisekunder = 3 timmar
    // för att få bakgrunden att byta kl 03:00 på morgonen.
    const now = new Date();
    const adjustedUnixTimestamp = now.getTime() - now.getTimezoneOffset() * 6e4 - 108e5;
    const currentMonth = new Date(adjustedUnixTimestamp).getUTCMonth();
    const seasonNum = [0, 0, 1, 1, 1, 2, 2, 2, 3, 3, 3, 0][currentMonth];
    const currentSeason = backgrounds[seasonNum];

    //  864e5 = 8,64*10^7 = 86 400 000 millisekunder = 24 timmar
    const fileNum = ((adjustedUnixTimestamp / 864e5) | 0) % currentSeason.files.length;
    const imageUrl = `backgrounds/${currentSeason.dir}/${currentSeason.files[fileNum]}.jpg`;

    preloadBackground(imageUrl);
  }
}
